import { createForm } from "@tanstack/solid-form";
import { produce } from "solid-js/store";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  FieldSectionTitle,
  Input,
} from "@/components/ui/input";
import { optionUrlCustomSchema } from "@/libs/zod-errors";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const validationSchema = z.object({
  website: optionUrlCustomSchema,
  portfolio: optionUrlCustomSchema,
  github: optionUrlCustomSchema,
  linkedin: optionUrlCustomSchema,
  instagram: optionUrlCustomSchema,
});

export const OnlinePrecense = () => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  const form = createForm(() => ({
    defaultValues: {
      website: store.online_presence.value.website,
      portfolio: store.online_presence.value.portfolio,
      github: store.online_presence.value.github,
      linkedin: store.online_presence.value.linkedin,
      instagram: store.online_presence.value.instagram,
    },
    schema: validationSchema,
    onSubmit: async ({ value }) => {
      const validatedData = validationSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.online_presence.value.website = validatedData.data.website;
          draft.online_presence.value.portfolio = validatedData.data.portfolio;
          draft.online_presence.value.github = validatedData.data.github;
          draft.online_presence.value.linkedin = validatedData.data.linkedin;
          draft.online_presence.value.instagram = validatedData.data.instagram;
          draft.online_presence.error = null;
        }),
      );
    },
  }));

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>
          {t("onboarding.steps.online-presence.title")}
        </OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.online-presence.description")}
        </OnboardingDescription>
      </div>
      <div class="flex flex-col gap-8 w-full">
        <form class="space-y-4">
          <div class="border-b border-neutral-200 pb-4 w-full space-y-4">
            <FieldSectionTitle class="text-md font-medium">
              Presenza online
            </FieldSectionTitle>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <form.Field
                name="website"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.website.safeParse(value);
                    if (result.success) {
                      setStore(
                        produce((draft) => {
                          draft.online_presence.value.website = value;
                          draft.online_presence.error = null;
                        }),
                      );
                    }
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="website">
                      {t("onboarding.steps.online-presence.website")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="url"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      class="w-full"
                    />
                    <FieldInfo
                      field={field()}
                      helpText={t(
                        "onboarding.steps.online-presence.websiteHelp",
                      )}
                    />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field
                name="portfolio"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.portfolio.safeParse(value);
                    if (result.success) {
                      setStore(
                        produce((draft) => {
                          draft.online_presence.value.portfolio = value;
                          draft.online_presence.error = null;
                        }),
                      );
                    }
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="portfolio">
                      {t("onboarding.steps.online-presence.portfolio")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="url"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder="https://yourportfolio.com"
                      class="w-full"
                    />
                    <FieldInfo
                      field={field()}
                      helpText={t(
                        "onboarding.steps.online-presence.portfolioHelp",
                      )}
                    />
                  </FieldContainer>
                )}
              </form.Field>
            </div>
          </div>

          <div class="border-b border-neutral-200 pb-4 w-full space-y-4">
            <FieldSectionTitle class="text-md font-medium">
              Social media
            </FieldSectionTitle>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <form.Field
                name="github"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.github.safeParse(value);
                    if (result.success) {
                      setStore(
                        produce((draft) => {
                          draft.online_presence.value.github = value;
                          draft.online_presence.error = null;
                        }),
                      );
                    }
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="github">
                      {t("onboarding.steps.online-presence.github")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="url"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder="https://github.com/username"
                      class="w-full"
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field
                name="linkedin"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.linkedin.safeParse(value);
                    if (result.success) {
                      setStore(
                        produce((draft) => {
                          draft.online_presence.value.linkedin = value;
                          draft.online_presence.error = null;
                        }),
                      );
                    }
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="linkedin">
                      {t("onboarding.steps.online-presence.linkedin")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="url"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder="https://linkedin.com/in/username"
                      class="w-full"
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              <form.Field
                name="instagram"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.instagram.safeParse(value);
                    if (result.success) {
                      setStore(
                        produce((draft) => {
                          draft.online_presence.value.instagram = value;
                          draft.online_presence.error = null;
                        }),
                      );
                    }
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="instagram">
                      {t("onboarding.steps.online-presence.instagram")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="url"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder="https://instagram.com/username"
                      class="w-full"
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
